import * as React from 'react';
import cn from 'classnames';
import { motion, Variants } from 'framer-motion';
import { observer } from 'mobx-react';

import {
  ManagerSegmentAnswerValue,
  ManagerSegmentSelectionMode
} from 'shared/entities/managerSegment';
import {
  TranslationKey,
  useTypedTranslation
} from 'shared/entities/localization';
import { useManagerSegmentStore } from 'stores/managerSegmentStore';
import Checkbox from 'shared/newComponents/form/Checkbox';
import Collapsed from 'shared/newComponents/Collapsed';
import RadioGroup from 'shared/newComponents/form/RadioGroup';

import OtherTextField from '../OtherTextField';
import StepFooter from '../StepFooter';
import cardStyles from '../OptionCard.modules.scss';

import './QuestionStep.modules.scss';

type Props = {
  chunkVariants: Variants;
  onNext: () => void;
  onBack: () => void;
};

const QuestionStep: React.FC<Props> = ({
  chunkVariants,
  onNext,
  onBack
}: Props) => {
  const store = useManagerSegmentStore();
  const { t } = useTypedTranslation('app');
  const question = store.currentQuestion;

  if (!question) {
    return null;
  }

  const isSingle =
    question.selectionMode === ManagerSegmentSelectionMode.single;
  const primaryDisabled =
    (store.isLastQuestion ? !store.canComplete : !store.canAdvanceQuestion) ||
    store.isSubmitting;
  const primaryAction = store.isLastQuestion ? store.complete : onNext;
  const primaryLabel = store.isLastQuestion
    ? t('managerSegment.actions.complete')
    : t('managerSegment.actions.continue');
  // Ключ собирается из вопроса и значения, статически его вывести нельзя
  const getAnswerLabel = (value: ManagerSegmentAnswerValue): string =>
    t(
      `managerSegment.questions.${question.question}.values.${value}` as TranslationKey<'app'>
    );

  return (
    <>
      <motion.div
        variants={chunkVariants}
        role={isSingle ? undefined : 'group'}
        aria-label={
          isSingle
            ? undefined
            : t(`managerSegment.questions.${question.question}.title`)
        }
        styleName="question-step__scroll"
      >
        {isSingle ? (
          <RadioGroup
            name={question.question}
            value={store.currentValues[0] ?? null}
            options={question.values.map((value) => ({
              id: value,
              label: getAnswerLabel(value)
            }))}
            onChange={store.selectSingleAnswer}
            disabled={store.isSubmitting}
            ariaLabel={t(`managerSegment.questions.${question.question}.title`)}
            itemClassName={cardStyles['option-card']}
          />
        ) : (
          <div styleName="question-step__options">
            {question.values.map((value) => {
              const selected = store.currentValues.includes(value);

              return (
                <Checkbox
                  key={value}
                  value={selected}
                  disabled={store.isSubmitting}
                  onChange={() => store.toggleMultipleAnswer(value)}
                  className={cn(
                    cardStyles['option-card'],
                    selected && cardStyles['option-card_selected']
                  )}
                  fullWidth
                  aligned
                >
                  {getAnswerLabel(value)}
                </Checkbox>
              );
            })}
          </div>
        )}

        {question.allowsOther && (
          <Collapsed opened={store.isOtherSelected}>
            <div styleName="question-step__other">
              <OtherTextField
                id="manager-segment-task-other"
                value={store.taskOtherText.value}
                error={store.taskOtherText.error}
                disabled={store.isSubmitting || !store.isOtherSelected}
                label={t('managerSegment.other.label')}
                placeholder={t('managerSegment.other.placeholder')}
                onChange={store.changeTaskOtherText}
              />
            </div>
          </Collapsed>
        )}
      </motion.div>

      <StepFooter
        chunkVariants={chunkVariants}
        primaryLabel={primaryLabel}
        primaryDisabled={primaryDisabled}
        primaryLoading={store.isLastQuestion && store.completeStage.isLoading}
        skipLoading={store.skipStage.isLoading}
        disabled={store.isSubmitting}
        onPrimary={primaryAction}
        onSkip={store.skip}
        onBack={onBack}
      />
    </>
  );
};

export default observer(QuestionStep);
